/* Find a church — for somebody who signed up without one.
 *
 * WHY THIS EXISTS. "Church you attend" is optional at signup, and choosing to
 * skip it is a real answer. But there was then no way to change your mind: the
 * Groups page said "Join one" with nothing to click, and the only route into a
 * church was waiting for somebody to invite you.
 *
 * JOINING IS IMMEDIATE, and that is deliberate — the same rule as signup. Pick
 * a church and you are a member; there is no request and no approval queue. A
 * church that wants to gate membership simply does not list itself publicly.
 * Approval exists for exactly one thing in this product, and it is claiming to
 * ADMINISTER a church, not attending one.
 *
 * ONE CHURCH AT A TIME. The API refuses a caller who already belongs to one
 * (409), because joining reassigns users.church_id without unwinding the
 * previous membership. So this page is only reachable when you have none, and
 * the sidebar link disappears once you do.
 *
 * THE CHURCH'S OWN QUESTIONS are asked here, the same form signup uses. A
 * church with none joins on one tap, as before; one with questions opens them
 * under its row first, and the server refuses a join that skips a required
 * one.
 */
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { AlertCircle, Church, Loader2, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { api, ApiError } from '@/api/client'
import { useMe } from '@/api/queries'
import type { CustomField, PublicChurch } from '@/types/api'
import {
  CustomFieldInputs,
  missingRequired,
} from '@/features/church/CustomFieldInputs'

export function FindChurchPage() {
  const navigate = useNavigate()
  const { data: me } = useMe()

  const [churches, setChurches] = useState<PublicChurch[]>([])
  const [loading, setLoading] = useState(true)
  const [query, setQuery] = useState('')
  const [joining, setJoining] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  /* The church whose questions are open, and the answers so far. */
  const [asking, setAsking] = useState<{
    church: PublicChurch
    fields: CustomField[]
  } | null>(null)
  const [values, setValues] = useState<Record<string, string>>({})

  useEffect(() => {
    let cancelled = false
    api
      .get<{ items: PublicChurch[] }>('/signup/churches/directory?limit=100', {
        auth: false,
      })
      .then((page) => !cancelled && setChurches(page.items))
      .catch(() => !cancelled && setError('The church list did not load.'))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [])

  /* Filtered here, not on the server: the directory is bounded and a round
     trip per keystroke would feel worse than instant local matching. */
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (!q) return churches
    return churches.filter(
      (c) =>
        c.name.toLowerCase().includes(q) ||
        (c.city ?? '').toLowerCase().includes(q),
    )
  }, [churches, query])

  async function start(church: PublicChurch) {
    if (joining) return
    setJoining(church.id)
    setError(null)
    let fields: CustomField[] = []
    try {
      const found = await api.get<{ custom_fields: CustomField[] }>(
        `/signup/${church.slug}`,
        { auth: false },
      )
      fields = found.custom_fields ?? []
    } catch {
      // Join anyway: if it does have required questions, the server says
      // which one, and that message is shown.
    }
    if (fields.length) {
      setValues({})
      setAsking({ church, fields })
      setJoining(null)
      return
    }
    await join(church, {})
  }

  async function join(church: PublicChurch, answers: Record<string, string>) {
    if (asking) {
      const unanswered = missingRequired(asking.fields, answers)
      if (unanswered.length) {
        setError(`Please answer “${unanswered[0].label}”.`)
        return
      }
    }
    setJoining(church.id)
    setError(null)
    try {
      await api.post(`/signup/${church.slug}/join`, { field_values: answers })
      // A full reload rather than a route change: church membership decides
      // what half the app shows, and every cached query was fetched by
      // somebody with no church.
      window.location.replace(import.meta.env.BASE_URL + 'groups')
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : 'Could not join that church. Please try again.',
      )
      setJoining(null)
    }
  }

  // Already in one — the API would refuse, so say so rather than showing a
  // list of buttons that all fail.
  if (me?.church_name) {
    return (
      <Card className="p-s8 text-center">
        <Church className="mx-auto h-8 w-8 text-ink-2" aria-hidden />
        <p className="mt-s3 text-md font-bold">You are already in a church</p>
        <p className="mt-1 text-base text-ink-2">
          You belong to {me.church_name}. Ask your church administrator if you
          need to move to a different one.
        </p>
        <Button
          variant="secondary"
          className="mt-s4"
          onClick={() => navigate('/groups')}
        >
          Go to Groups
        </Button>
      </Card>
    )
  }

  return (
    <div className="space-y-s4">
      <header>
        <h1 className="text-2xl font-bold tracking-tight">Find a church</h1>
        <p className="mt-1 text-base text-ink-2">
          Join one to see its groups, events and prayer requests.
        </p>
      </header>

      {error ? (
        <div
          role="alert"
          className="flex items-start gap-s2 rounded border border-danger/30 bg-danger-soft p-s3 text-base text-danger"
        >
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>{error}</span>
        </div>
      ) : null}

      <Card className="flex items-center gap-s3 p-s3">
        <div className="relative min-w-[200px] flex-1">
          <Search
            className="pointer-events-none absolute left-s3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-2"
            aria-hidden
          />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name or city"
            aria-label="Search churches"
            className="w-full rounded-pill border border-line-2 bg-surface-2 py-s2 pl-[38px] pr-s3 text-base outline-none focus:shadow-focus"
          />
        </div>
      </Card>

      {loading ? (
        <Card className="flex justify-center py-s6">
          <Loader2 className="h-5 w-5 animate-spin text-ink-2" aria-label="Loading" />
        </Card>
      ) : null}

      {!loading && !churches.length ? (
        <Card className="px-s4 py-s6 text-center text-base text-ink-2">
          No churches are listed yet. When one is added you will be able to join
          it here — or accept an invitation if somebody sends you one.
        </Card>
      ) : null}

      {!loading && churches.length && !shown.length ? (
        <Card className="px-s4 py-s6 text-center text-base text-ink-2">
          No church matches “{query}”.
        </Card>
      ) : null}

      {shown.length ? (
        <ul className="flex flex-col gap-s2">
          {shown.map((c) => (
            <li key={c.id} className="cn-card px-s4 py-s3">
              <div className="flex items-center gap-s3">
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-pill bg-brand-soft text-brand">
                  <Church className="h-5 w-5" aria-hidden />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-md font-semibold">
                    {c.name}
                  </span>
                  {c.city ? (
                    <span className="block truncate text-sm text-ink-2">
                      {c.city}
                    </span>
                  ) : null}
                </span>
                {asking?.church.id !== c.id ? (
                  <Button
                    variant="primary"
                    loading={joining === c.id}
                    disabled={Boolean(joining) && joining !== c.id}
                    onClick={() => start(c)}
                  >
                    Join
                  </Button>
                ) : null}
              </div>
              {asking?.church.id === c.id ? (
                <form
                  className="mt-s4 space-y-s4 border-t border-line-2 pt-s4"
                  onSubmit={(e) => {
                    e.preventDefault()
                    join(c, values)
                  }}
                >
                  <p className="text-base text-ink-2">
                    {c.name} asks a few questions of everyone who joins.
                  </p>
                  <CustomFieldInputs
                    fields={asking.fields}
                    values={values}
                    onChange={setValues}
                    disabled={joining === c.id}
                  />
                  <div className="flex justify-end gap-s2">
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={joining === c.id}
                      onClick={() => {
                        setAsking(null)
                        setError(null)
                      }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      loading={joining === c.id}
                    >
                      Join {c.name}
                    </Button>
                  </div>
                </form>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}
